import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ToggleLeft, ToggleRight, Pencil, ExternalLink, AlertCircle } from "lucide-react";
import { authHeader } from "@/hooks/useAdminAuth";
import API from "@/lib/api";
import { PAGE_SCHEMAS, PAGE_LABELS } from "./page-schemas";

export default function AdminPageSections() {
  const { page } = useParams();
  const navigate = useNavigate();
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const knownPage = !!PAGE_SCHEMAS[page];

  const load = () => {
    if (!knownPage) {
      setLoading(false);
      return;
    }
    setLoading(true);
    axios
      .get(`${API}/pages/${page}/sections?admin=true`, { headers: authHeader() })
      .then(({ data }) => setSections(Array.isArray(data) ? data : []))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(load, [page]); // eslint-disable-line

  const toggle = async (s) => {
    try {
      await axios.put(
        `${API}/pages/${page}/sections/${s.key}`,
        { enabled: !s.enabled },
        { headers: authHeader() }
      );
      setSections((arr) => arr.map((x) => (x.key === s.key ? { ...x, enabled: !s.enabled } : x)));
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    }
  };

  if (!knownPage) {
    return (
      <div className="text-white">
        <div className="flex items-start gap-2 px-4 py-3 rounded-lg border bg-red-400/10 border-red-400/30 text-red-300 text-sm">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          Unknown page: <code>{page}</code>
        </div>
        <button onClick={() => navigate("/ngxp-admin/pages/home")} className="mt-4 text-[#00A8E8] underline text-sm">
          Go to Home Page
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#7B2CBF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const livePath = page === "home" || page === "footer" ? "/" : `/${page}`;

  return (
    <div>
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-white font-display">{PAGE_LABELS[page]} — Sections</h1>
          <p className="text-gray-500 mt-1 text-sm">
            {sections.length} sections — toggle visibility, reorder, or edit content. Visual design is preserved.
          </p>
          <div className="mt-3 flex gap-2 flex-wrap">
            {Object.keys(PAGE_SCHEMAS).map((p) => (
              <Link
                key={p}
                to={`/ngxp-admin/pages/${p}`}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                  p === page
                    ? "bg-[#7B2CBF]/20 text-white border-[#7B2CBF]/40"
                    : "bg-white/5 text-gray-400 border-white/10 hover:bg-white/10 hover:text-white"
                }`}
              >
                {PAGE_LABELS[p]}
              </Link>
            ))}
          </div>
        </div>
        <a
          href={livePath}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-white/5 text-white border border-white/10 hover:bg-white/10 transition-colors"
        >
          <ExternalLink className="w-4 h-4" /> View Live
        </a>
      </div>

      <div className="space-y-3">
        {sections.map((s, i) => (
          <div
            key={s.key}
            className={`bg-[#0D0E13] border border-white/5 rounded-xl px-5 py-4 flex items-center gap-4 transition-opacity ${
              s.enabled ? "" : "opacity-50"
            }`}
          >
            <div className="w-10 h-10 rounded-xl bg-[#7B2CBF]/15 border border-[#7B2CBF]/25 flex items-center justify-center shrink-0 text-[#7B2CBF] text-xs font-bold">
              {String(i + 1).padStart(2, "0")}
            </div>

            <div className="flex-1 min-w-0">
              <div className="text-white font-medium text-sm">{s.label}</div>
              <div className="text-gray-500 text-xs mt-0.5 font-mono">{s.key}</div>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${
                s.enabled
                  ? "bg-green-400/15 text-green-400 border-green-400/25"
                  : "bg-gray-500/15 text-gray-400 border-gray-500/25"
              }`}>
                {s.enabled ? "Visible" : "Hidden"}
              </span>
              <button onClick={() => toggle(s)} className="text-gray-500 hover:text-white transition-colors p-1"
                title={s.enabled ? "Hide" : "Show"}>
                {s.enabled ? <ToggleRight className="w-6 h-6 text-green-400" /> : <ToggleLeft className="w-6 h-6" />}
              </button>
              <Link to={`/ngxp-admin/pages/${page}/${s.key}`}
                className="text-gray-500 hover:text-[#00A8E8] transition-colors p-1" title="Edit">
                <Pencil className="w-4 h-4" />
              </Link>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
